import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PackLanguage, StarterPack } from '@b4b/shared';
import { useStarterPacks } from '../api/queries.js';

/** Feed URLs compared without scheme, `www.`, trailing slash and fragment; host case-insensitive.
 * The server keeps the exact string, this is only for "do I already follow this?" checks. */
export function normalizeFeedUrl(url: string): string {
  try {
    const u = new URL(url.trim());
    const host = u.host.toLowerCase().replace(/^www\./, '');
    return `${host}${u.pathname.replace(/\/+$/, '')}${u.search}`;
  } catch {
    return url.trim().replace(/\/+$/, '');
  }
}

export interface PackSubscription {
  /** how many of the pack's feeds the user already subscribes to */
  subscribed: number;
  total: number;
  /** every feed of the pack is subscribed */
  complete: boolean;
  isSubscribed: (feedUrl: string) => boolean;
}

/** Which of a pack's feeds the user already has, given their subscription URLs. */
export function packSubscription(pack: StarterPack, subscribedUrls: Iterable<string>): PackSubscription {
  const have = new Set(Array.from(subscribedUrls, normalizeFeedUrl));
  const isSubscribed = (feedUrl: string) => have.has(normalizeFeedUrl(feedUrl));
  const subscribed = pack.feeds.filter((f) => isSubscribed(f.url)).length;
  return { subscribed, total: pack.feeds.length, complete: pack.feeds.length > 0 && subscribed === pack.feeds.length, isSubscribed };
}

/** Packs exist only in cs/en — Czech UI gets the Czech set first, every other language the international one. */
export function packLanguageFor(uiLanguage: string): PackLanguage {
  return uiLanguage.startsWith('cs') ? 'cs' : 'en';
}

export interface PackGroup {
  /** i18n key suffix under feed.empty: localPacks | foreignPacks */
  key: 'localPacks' | 'foreignPacks';
  packs: StarterPack[];
}

/** Starter packs grouped for display: the user's language first, the rest under "international". */
export function groupPacks(packs: StarterPack[], lang: PackLanguage): PackGroup[] {
  return [
    { key: 'localPacks' as const, packs: packs.filter((p) => p.lang === lang) },
    { key: 'foreignPacks' as const, packs: packs.filter((p) => p.lang !== lang) },
  ].filter((g) => g.packs.length > 0);
}

/** Shared by the empty feed state and the Sources page. */
export function usePackGroups(): { lang: PackLanguage; groups: PackGroup[]; isLoading: boolean } {
  const { i18n } = useTranslation();
  const packs = useStarterPacks();
  const lang = packLanguageFor(i18n.language);
  const groups = useMemo(() => groupPacks(packs.data ?? [], lang), [packs.data, lang]);
  return { lang, groups, isLoading: packs.isPending };
}
